import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Plus, 
  Upload,
  MapPin,
  Calendar,
  Clock,
  Users,
  User,
  FileSpreadsheet,
  Trash2,
  Eye,
  Edit,
  Download,
  Check,
  X,
  FileText,
  Image as ImageIcon,
  File,
  CheckCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CampsPage({ user, logout }) {
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showPTAModal, setShowPTAModal] = useState(false);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [campLeads, setCampLeads] = useState([]);
  const [audiologists, setAudiologists] = useState([]);
  const fileInputRef = useRef(null);
  const ptaInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [ptaUploading, setPtaUploading] = useState(false);

  const [newCamp, setNewCamp] = useState({
    name: "",
    date: "",
    start_time: "",
    end_time: "",
    location_name: "",
    location_address: "",
    map_link: "",
    audiologist_id: "",
    audiologist_name: "",
    notes: ""
  });

  const [newLead, setNewLead] = useState({
    name: "",
    phone: "",
    email: "",
    notes: ""
  });

  useEffect(() => {
    fetchCamps();
    fetchAudiologists();
  }, []);

  const fetchCamps = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/camps`, { withCredentials: true });
      setCamps(response.data.camps || []);
    } catch (error) {
      toast.error("Failed to load camps");
    } finally {
      setLoading(false);
    }
  };

  const fetchAudiologists = async () => {
    try {
      const response = await axios.get(`${API}/team/users`, { withCredentials: true });
      setAudiologists(response.data.users || []);
    } catch (error) {
      console.error("Failed to fetch audiologists");
    }
  };

  const fetchCampLeads = async (campId) => {
    try {
      const response = await axios.get(`${API}/camps/${campId}/leads`, { withCredentials: true });
      setCampLeads(response.data.leads || []);
    } catch (error) {
      toast.error("Failed to load camp leads");
    }
  };

  const handleCreateCamp = async () => {
    if (!newCamp.name || !newCamp.date) {
      toast.error("Camp name and date are required");
      return;
    }
    try {
      const response = await axios.post(`${API}/camps`, newCamp, { withCredentials: true });
      toast.success("Camp created successfully!");
      setShowCreateModal(false);
      setNewCamp({
        name: "", date: "", start_time: "", end_time: "",
        location_name: "", location_address: "", map_link: "",
        audiologist_id: "", audiologist_name: "", notes: ""
      });
      fetchCamps();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create camp");
    }
  };

  const handleAddLead = async () => {
    if (!newLead.name || !newLead.phone) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      await axios.post(`${API}/camps/${selectedCamp.camp_id}/leads`, newLead, { withCredentials: true });
      toast.success("Lead added successfully!");
      setShowAddLeadModal(false);
      setNewLead({ name: "", phone: "", email: "", notes: "" });
      fetchCampLeads(selectedCamp.camp_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add lead");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCamp) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploadProgress(0);
    try {
      const response = await axios.post(
        `${API}/camps/${selectedCamp.camp_id}/upload-leads`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );
      toast.success(`${response.data.imported} leads imported successfully!`);
      setShowUploadModal(false);
      fetchCampLeads(selectedCamp.camp_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload leads");
    } finally {
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteCamp = async (campId) => {
    if (!confirm("Are you sure you want to delete this camp?")) return;
    try {
      await axios.delete(`${API}/camps/${campId}`, { withCredentials: true });
      toast.success("Camp deleted");
      fetchCamps();
    } catch (error) {
      toast.error("Failed to delete camp");
    }
  };

  const openCampLeads = (camp) => {
    setSelectedCamp(camp);
    fetchCampLeads(camp.camp_id);
    setShowLeadsModal(true);
  };

  const downloadTemplate = () => {
    const template = "Name,Phone,Email,Notes\nJohn Doe,9876543210,john@example.com,Sample note";
    const blob = new Blob([template], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "camp_leads_template.csv";
    a.click();
  };

  // PTA Report Upload
  const openPTAUpload = (lead) => {
    setSelectedLead(lead);
    setShowPTAModal(true);
  };

  const handlePTAUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedLead || !selectedCamp) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/heic'];
    const fileExt = file.name.split('.').pop().toLowerCase();
    const allowedExts = ['pdf', 'jpg', 'jpeg', 'png', 'heic'];
    
    if (!allowedExts.includes(fileExt)) {
      toast.error("Invalid file type. Please upload PDF or image (JPG, PNG, HEIC)");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB");
      return;
    }

    setPtaUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API}/camps/${selectedCamp.camp_id}/lead/${selectedLead.lead_id}/pta-report`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      toast.success("PTA Report uploaded successfully!");
      setShowPTAModal(false);
      // Refresh leads to show updated PTA status
      fetchCampLeads(selectedCamp.camp_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload PTA report");
    } finally {
      setPtaUploading(false);
      if (ptaInputRef.current) ptaInputRef.current.value = "";
    }
  };

  const viewPTAReport = async (lead) => {
    try {
      const response = await axios.get(
        `${API}/camps/${selectedCamp.camp_id}/lead/${lead.lead_id}/pta-report`,
        { withCredentials: true }
      );
      
      const report = response.data;
      if (report.content_base64) {
        // Open in new tab
        const byteCharacters = atob(report.content_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: report.file_type });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch (error) {
      toast.error("Failed to load PTA report");
    }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="camps-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Camp Management</h1>
            <p className="text-slate-600 mt-1">Create and manage hearing camps with leads</p>
          </div>
          <Button 
            className="bg-cyan-700 hover:bg-cyan-800"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-camp-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Camp
          </Button>
        </div>

        {/* Camps Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : camps.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-400" />
              <h3 className="text-lg font-medium text-slate-900">No Camps Yet</h3>
              <p className="text-slate-500 mt-1">Create your first hearing camp to get started</p>
              <Button 
                className="mt-4 bg-cyan-700 hover:bg-cyan-800"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Camp
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {camps.map((camp) => (
              <Card key={camp.camp_id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{camp.name}</CardTitle>
                      <Badge 
                        className={
                          new Date(camp.date) > new Date() 
                            ? "bg-green-100 text-green-800 mt-2" 
                            : "bg-slate-100 text-slate-600 mt-2"
                        }
                      >
                        {new Date(camp.date) > new Date() ? "Upcoming" : "Past"}
                      </Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openCampLeads(camp)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteCamp(camp.camp_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Calendar className="w-4 h-4" />
                      <span>{format(new Date(camp.date), "MMM dd, yyyy")}</span>
                      {camp.start_time && (
                        <>
                          <Clock className="w-4 h-4 ml-2" />
                          <span>{camp.start_time} - {camp.end_time || "End"}</span>
                        </>
                      )}
                    </div>
                    {camp.location_name && (
                      <div className="flex items-start gap-2 text-slate-600">
                        <MapPin className="w-4 h-4 mt-0.5" />
                        <div>
                          <p className="font-medium">{camp.location_name}</p>
                          {camp.location_address && <p className="text-xs">{camp.location_address}</p>}
                        </div>
                      </div>
                    )}
                    {camp.audiologist_name && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <User className="w-4 h-4" />
                        <span>{camp.audiologist_name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-slate-600">
                      <Users className="w-4 h-4" />
                      <span className="font-medium">{camp.leads_count || 0} Leads</span>
                    </div>
                    {camp.map_link && (
                      <a 
                        href={camp.map_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-cyan-700 hover:underline text-xs"
                      >
                        Open in Google Maps →
                      </a>
                    )}
                  </div>
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => { setSelectedCamp(camp); setShowAddLeadModal(true); fetchCampLeads(camp.camp_id); }}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Lead
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => { setSelectedCamp(camp); setShowUploadModal(true); }}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Camp Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Camp</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Camp Name *</label>
                <Input
                  placeholder="e.g., Delhi North Hearing Camp"
                  value={newCamp.name}
                  onChange={(e) => setNewCamp({ ...newCamp, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Date *</label>
                  <Input
                    type="date"
                    value={newCamp.date}
                    onChange={(e) => setNewCamp({ ...newCamp, date: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium">Start Time</label>
                    <Input
                      type="time"
                      value={newCamp.start_time}
                      onChange={(e) => setNewCamp({ ...newCamp, start_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">End Time</label>
                    <Input
                      type="time"
                      value={newCamp.end_time}
                      onChange={(e) => setNewCamp({ ...newCamp, end_time: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Location Name</label>
                <Input
                  placeholder="e.g., Community Center Hall"
                  value={newCamp.location_name}
                  onChange={(e) => setNewCamp({ ...newCamp, location_name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Full Address</label>
                <Textarea
                  placeholder="Complete address for the camp location"
                  value={newCamp.location_address}
                  onChange={(e) => setNewCamp({ ...newCamp, location_address: e.target.value })}
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Google Maps Link</label>
                <Input
                  placeholder="https://maps.google.com/..."
                  value={newCamp.map_link}
                  onChange={(e) => setNewCamp({ ...newCamp, map_link: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Audiologist</label>
                <Select 
                  value={newCamp.audiologist_id} 
                  onValueChange={(value) => {
                    const aud = audiologists.find(a => a.user_id === value);
                    setNewCamp({ 
                      ...newCamp, 
                      audiologist_id: value,
                      audiologist_name: aud?.name || ""
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select audiologist" />
                  </SelectTrigger>
                  <SelectContent>
                    {audiologists.map((aud) => (
                      <SelectItem key={aud.user_id} value={aud.user_id}>{aud.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  placeholder="Any additional notes about the camp"
                  value={newCamp.notes}
                  onChange={(e) => setNewCamp({ ...newCamp, notes: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-cyan-700 hover:bg-cyan-800" onClick={handleCreateCamp}>
                  Create Camp
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Upload Leads Modal */}
        <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Leads to {selectedCamp?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                <p className="text-slate-600 mb-4">Upload a CSV or Excel file with leads</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Select File
                </Button>
                {uploadProgress !== null && (
                  <div className="mt-4">
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-cyan-700 h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{uploadProgress}% uploaded</p>
                  </div>
                )}
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Required Columns:</p>
                <p className="text-sm text-slate-600">Name, Phone</p>
                <p className="text-sm text-slate-600 mt-1">Optional: Email, Notes</p>
                <Button variant="link" size="sm" className="p-0 mt-2" onClick={downloadTemplate}>
                  <Download className="w-3 h-3 mr-1" />
                  Download Template
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Single Lead Modal */}
        <Dialog open={showAddLeadModal} onOpenChange={setShowAddLeadModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Lead to {selectedCamp?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Name *</label>
                <Input
                  placeholder="Lead name"
                  value={newLead.name}
                  onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Phone *</label>
                <Input
                  placeholder="10-digit phone number"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input
                  type="email"
                  placeholder="Email (optional)"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  placeholder="Any notes about this lead"
                  value={newLead.notes}
                  onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowAddLeadModal(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-cyan-700 hover:bg-cyan-800" onClick={handleAddLead}>
                  Add Lead
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Camp Leads Modal */}
        <Dialog open={showLeadsModal} onOpenChange={setShowLeadsModal}>
          <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>{selectedCamp?.name} - Leads ({campLeads.length})</span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowAddLeadModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowUploadModal(true)}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload
                  </Button>
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto max-h-[60vh] mt-4">
              {campLeads.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No leads in this camp yet</p>
                  <p className="text-sm mt-1">Add leads manually or upload a file</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Disposition</TableHead>
                      <TableHead>PTA Report</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campLeads.map((lead) => (
                      <TableRow key={lead.lead_id}>
                        <TableCell className="font-medium">{lead.name || "Unknown"}</TableCell>
                        <TableCell>{lead.phone}</TableCell>
                        <TableCell>{lead.email || "-"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {lead.disposition?.replace("_", " ") || "Not Contacted"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {lead.has_pta_report ? (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-green-600 hover:text-green-700 hover:bg-green-50 p-1 h-auto"
                              onClick={() => viewPTAReport(lead)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 p-1 h-auto"
                              onClick={() => openPTAUpload(lead)}
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Upload
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-600 text-sm">
                          {lead.created_at ? format(new Date(lead.created_at), "dd MMM yyyy") : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* PTA Report Upload Modal */}
        <Dialog open={showPTAModal} onOpenChange={setShowPTAModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upload PTA Report</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium text-slate-800">{selectedLead?.name}</p>
                <p className="text-sm text-slate-600">{selectedLead?.phone}</p>
              </div>
              
              <div className="border-2 border-dashed border-cyan-200 rounded-lg p-8 text-center bg-cyan-50/50">
                <FileText className="w-12 h-12 mx-auto mb-4 text-cyan-500" />
                <p className="text-slate-600 mb-2">Upload PTA Report</p>
                <p className="text-sm text-slate-500 mb-4">PDF or Image (JPG, PNG, HEIC) - Max 10MB</p>
                <input
                  ref={ptaInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.heic"
                  onChange={handlePTAUpload}
                  className="hidden"
                />
                <Button 
                  onClick={() => ptaInputRef.current?.click()}
                  disabled={ptaUploading}
                  className="bg-cyan-700 hover:bg-cyan-800"
                >
                  {ptaUploading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Select File
                    </>
                  )}
                </Button>
              </div>
              
              <div className="flex gap-2 text-xs text-slate-500">
                <FileText className="w-4 h-4" />
                <span>Supported: PDF</span>
                <ImageIcon className="w-4 h-4 ml-2" />
                <span>JPG, PNG, HEIC</span>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
